// front/src/app/core/layout/application-layout/dev-tools/dev-tools-panel.component.ts
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import {
  DevToolsService,
  DevToolsStatus,
  Snapshot,
  CurrentOperation,
  ImpersonationCandidate,
} from './dev-tools.service';
import { of, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, takeWhile } from 'rxjs/operators';
import { AuthService } from '../../../services/auth.service';
import { DevImpersonationState } from '../../../models/user-profile.model';

@Component({
  selector: 'app-dev-tools-panel',
  templateUrl: './dev-tools-panel.component.html',
  styleUrls: ['./dev-tools-panel.component.scss'],
})
export class DevToolsPanelComponent implements OnInit, OnDestroy {
  @Input() status: DevToolsStatus;
  @Output() closed = new EventEmitter<void>();
  @Output() statusChanged = new EventEmitter<void>();

  snapshots: Snapshot[] = [];
  currentOperation: CurrentOperation | null = null;
  isCreatingSnapshot = false;
  newSnapshotName = '';
  pollSubscription: Subscription | null = null;

  // Substitution d'identité
  impersonationSearch = '';
  impersonationCandidates: ImpersonationCandidate[] = [];
  private impersonationSearch$ = new Subject<string>();
  private impersonationSearchSubscription: Subscription | null = null;

  constructor(
    private devToolsService: DevToolsService,
    private authService: AuthService,
  ) {}

  ngOnInit(): void {
    this.loadSnapshots();
    this.checkCurrentOperation();
    this.impersonationSearchSubscription = this.impersonationSearch$
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
        switchMap(term => (term.length < 2 ? of([]) : this.devToolsService.getImpersonationCandidates(term))),
      )
      .subscribe(candidates => {
        this.impersonationCandidates = candidates;
      });
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.impersonationSearchSubscription?.unsubscribe();
  }

  loadSnapshots(): void {
    this.devToolsService.getSnapshots().subscribe(snapshots => {
      this.snapshots = snapshots;
    });
  }

  get manualSnapshots(): Snapshot[] {
    return this.snapshots.filter(s => s.type === 'manual');
  }

  /** Substitution active, telle que renvoyée par /me. */
  get impersonation(): DevImpersonationState | null {
    return this.authService.currentUserValue()?.devImpersonation ?? null;
  }

  onImpersonationSearch(term: string): void {
    this.impersonationSearch$.next((term ?? '').trim());
  }

  impersonate(candidate: ImpersonationCandidate): void {
    this.devToolsService.startImpersonation(candidate._id).subscribe(() => this.reloadApp());
  }

  stopImpersonation(): void {
    this.devToolsService.stopImpersonation().subscribe(() => this.reloadApp());
  }

  checkCurrentOperation(): void {
    this.devToolsService.getCurrentOperation().subscribe(op => {
      this.currentOperation = op;
      if (op && !op.error) {
        this.startPolling();
      }
    });
  }

  pull(environment: string): void {
    const hasExisting = this.snapshots.some(s => s.type === 'auto' && s.environment === environment);
    const message = hasExisting
      ? `Écraser le snapshot ${environment} existant et restaurer les données distantes ?`
      : `Récupérer les données de ${environment} et les restaurer localement ?`;

    if (!confirm(message)) return;

    // Lancer le pull (fire-and-forget) — le polling gère la détection de fin
    this.devToolsService.pull(environment).subscribe({
      error: () => this.checkCurrentOperation(),
    });

    // Démarrer le polling immédiatement
    this.currentOperation = { type: 'pull', environment, step: 'Démarrage...', progress: 0, total: 1 };
    this.startPolling();
  }

  createSnapshot(): void {
    if (!this.newSnapshotName.trim()) return;

    this.devToolsService.createSnapshot(this.newSnapshotName.trim()).subscribe({
      error: () => this.checkCurrentOperation(),
    });

    this.newSnapshotName = '';
    this.isCreatingSnapshot = false;
    this.currentOperation = { type: 'snapshot', step: 'Démarrage...', progress: 0, total: 1 };
    this.startPolling();
  }

  switchSnapshot(snapshotId: string): void {
    if (!confirm('Restaurer ce snapshot ? Les données locales seront écrasées.')) return;

    this.devToolsService.switchSnapshot(snapshotId).subscribe({
      error: () => this.checkCurrentOperation(),
    });

    this.currentOperation = { type: 'switch', step: 'Démarrage...', progress: 0, total: 1 };
    this.startPolling();
  }

  deleteSnapshot(snapshotId: string, name: string): void {
    if (!confirm(`Supprimer le snapshot "${name}" ?`)) return;

    this.devToolsService.deleteSnapshot(snapshotId).subscribe(() => {
      this.loadSnapshots();
      this.statusChanged.emit();
    });
  }

  clearError(): void {
    this.devToolsService.clearOperation().subscribe(() => {
      this.currentOperation = null;
    });
  }

  private startPolling(): void {
    this.stopPolling();
    let isPolling = true;
    this.pollSubscription = this.devToolsService
      .pollOperation()
      .pipe(takeWhile(() => isPolling))
      .subscribe(op => {
        this.currentOperation = op;
        if (!op || op.error) {
          isPolling = false;
          if (!op) {
            this.onOperationComplete();
          }
        }
      });
  }

  private stopPolling(): void {
    if (this.pollSubscription) {
      this.pollSubscription.unsubscribe();
      this.pollSubscription = null;
    }
  }

  private onOperationComplete(): void {
    this.stopPolling();
    this.currentOperation = null;
    this.reloadApp();
  }

  /** Rediriger vers ticket-home puis reload pour vider les caches Angular (données ou identité changées). */
  private reloadApp(): void {
    window.location.href = '/ticket-home';
  }

  get progressPercent(): number {
    if (!this.currentOperation || this.currentOperation.total === 0) return 0;
    return Math.round((this.currentOperation.progress / this.currentOperation.total) * 100);
  }
}
